import React, { useEffect, useState } from "react";
import { Box, Card, TextField, Typography, CardContent, Grid, Divider } from "@material-ui/core";
import { useAppDispatch, useAppSelector } from "src/app-redux/hooks";
import TabHeader from "src/components/TabHeader/TabHeader";
import { ManagementRoute } from "../../management.routes";
import { useStyles } from "./components/style";
import Backdrop from "@material-ui/core/Backdrop";
import CommonButton from "src/components/CommonButton/CommonButton";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import { useFormik } from "formik";
import { number, object, string } from "yup";
import { disableCopy, disablePaste } from "src/common/utils/formValidation";
import {
  getNotificationType,
  updateNotificationType,
} from "src/app-redux/management/actions/managementActions";
import { loading } from "src/app-redux/management/managementSlice";
import CommonLoader from "src/components/Loader/CommonLoader";

type Props = {};

export function General({}: Props): React.ReactElement {
  const classes = useStyles();
  const moduleName = ManagementRoute.title;
  const pageHeading = "General";
  const dispatch = useAppDispatch();
  const loader = useAppSelector(loading);

  const [notificationTypeList, setNotificationTypeList] = useState<any>([]);

  const validationSchema = object().shape({
    cameraFeed: string().max(200, "Maximum 200 characters allowed"),
    reminderNotificationMinuts: number().when("reminderNotificationHour", {
      is: (value: any) => {
        if (formik.values.overStayNotification && !value) {
          return true;
        }
        // return !value;
      },
      then: number()
        .required("This field is required")
        .min(1, "Minimum 1 minutes allowed")
        .max(60, "Maximum 60 minutes allowed"),
      otherwise: number().min(1, "Minimum 1 minutes allowed").max(60, "Maximum 60 minutes allowed"),
    }),
    reminderNotificationHour: number().max(100, "Maximum 100 hours allowed"),
    fistNotificationMinuts: number().when("fistNotificationHour", {
      is: (value: any) => {
        if (formik.values.overStayNotification && !value) {
          return true;
        }
        // return !value;
      },
      then: number()
        .required("This field is required")
        .min(1, "Minimum 1 minutes allowed")
        .max(60, "Maximum 60 minutes allowed"),
      otherwise: number().min(1, "Minimum 1 minutes allowed").max(60, "Maximum 60 minutes allowed"),
    }),
    fistNotificationHour: number()
      // .min(1, "Minimum 1 hour allowed")
      .max(100, "Maximum 100 hours allowed"),
    btAllias: string()
      .required("This field is required")
      .min(3, "Minimum 3 characters allowed")
      .max(5, "Maximum 5 characters allowed"),
  });

  const formik = useFormik<any>({
    enableReinitialize: true,
    initialValues: {
      cameraFeed: "",
      btAllias: "",
      // fistNotificationTime: 0,
      // reminderNotificationTime: 0,
      fistNotificationHour: "",
      fistNotificationMinuts: "",
      reminderNotificationHour: "",
      reminderNotificationMinuts: "",
      overStayNotification: 0,
      illicitFactorNotification: 0,
    },
    validationSchema: validationSchema,
    onSubmit: async (values: any, actions) => {
      const fistNotificationTime =
        Number(values.fistNotificationHour) * 60 + Number(values.fistNotificationMinuts);
      const reminderNotificationTime =
        Number(values.reminderNotificationHour) * 60 + Number(values.reminderNotificationMinuts);
      const isActiveOverStay = values.overStayNotification ? 1 : 0;
      const isActiveReminder = values.illicitFactorNotification ? 1 : 0;
      const notificationTypes = await transformedNotificationTypes(
        fistNotificationTime,
        reminderNotificationTime,
        isActiveOverStay,
        isActiveReminder
      );

      const body: any = await {
        cameraFeed: values.cameraFeed,
        terminalAlias: values.btAllias,
        notificationTypes: notificationTypes,
      };
      const res: any = await dispatch(updateNotificationType(body));
      await resetForm();
      getNotificationTypeHandler();
      if (res.payload) {
        actions.setSubmitting(false);
        return;
      }
    },
  });

  const resetForm = () => {
    formik.resetForm();
    // formik.setValues(formik.initialValues);
  };

  const convertToMinutes = (minutes: any) => {
    const remainingMinutes = minutes % 60; // Calculate the remaining minutes

    return remainingMinutes;
  };

  const convertToHour = (minutes: any) => {
    const hours = Math.floor(minutes / 60); // Calculate the whole hours

    return hours;
  };

  const transformedNotificationTypes = async (
    fistNotificationTime: number,
    reminderNotificationTime: number,
    overStayNotification: number,
    illicitFactorNotification: number
  ) => {
    return notificationTypeList.map((data: any) => {
      if (data.type === 1) {
        data.notificationTime = fistNotificationTime;
        data.reminderTime = reminderNotificationTime;
        data.isActive = overStayNotification;
      }
      if (data.type === 2) {
        data.isActive = illicitFactorNotification;
      }
      return data;
    });
  };

  const notificationTypes = async (notification: any, type: number) => {
    return notification.find((data: any) => data.type === type);
  };

  const fieldValueHandler = async (notification: any) => {
    const overStayNotification = await notificationTypes(notification?.notificationTypes, 1);
    const illicitFactorNotification = await notificationTypes(notification?.notificationTypes, 2);

    const fistNotificationHour = await convertToHour(
      Number(overStayNotification?.notificationTime)
    );

    const fistNotificationMinuts = await convertToMinutes(
      Number(overStayNotification?.notificationTime)
    );
    const reminderNotificationHour = await convertToHour(
      Number(overStayNotification?.reminderTime)
    );

    const reminderNotificationMinuts = await convertToMinutes(
      Number(overStayNotification?.reminderTime)
    );

    if (fistNotificationHour) {
      formik.setFieldValue("fistNotificationHour", Number(fistNotificationHour));
    }
    if (fistNotificationMinuts) {
      formik.setFieldValue("fistNotificationMinuts", Number(fistNotificationMinuts));
    }

    if (reminderNotificationHour) {
      formik.setFieldValue("reminderNotificationHour", Number(reminderNotificationHour));
    }
    if (reminderNotificationMinuts) {
      formik.setFieldValue("reminderNotificationMinuts", Number(reminderNotificationMinuts));
    }

    formik.setFieldValue("overStayNotification", Boolean(overStayNotification?.isActive || false));
    formik.setFieldValue(
      "illicitFactorNotification",
      Boolean(illicitFactorNotification?.isActive || false)
    );
    formik.setFieldValue("btAllias", notification.terminalAlias || "");
    formik.setFieldValue("cameraFeed", notification.cameraFeed || "");
    return;
  };

  const getNotificationTypeHandler = async () => {
    try {
      const res = await dispatch(getNotificationType());
      if (res.payload) {
        fieldValueHandler(res.payload);
        setNotificationTypeList(res.payload.notificationTypes);
        return;
      }
    } catch (error) {
      throw error;
    }
  };

  useEffect(() => {
    getNotificationTypeHandler();
  }, []);

  const listener = (e: any) => {
    if (
      (e.key === "s" || e.key === "S") &&
      (navigator.platform.match("Mac") ? e.metaKey : e.ctrlKey)
    ) {
      e.preventDefault();
      formik.submitForm();
    }
  };

  useEffect(() => {
    document.addEventListener("keydown", listener);
    return () => {
      document.removeEventListener("keydown", listener);
    };
  }, []);

  return (
    <>
      <Box m={2} />

      {/* <Prompt when={isDirty} message="Changes you made may not be saved." /> */}
      <TabHeader module={moduleName} heading={pageHeading} link={ManagementRoute.path} />
      <Backdrop className={classes.backdrop} open={loader}>
        <CommonLoader />
      </Backdrop>
      <Box m={2} />

      <Card>
        <CardContent className={classes.cardMain}>
          <Grid container direction="row" justifyContent="space-between" alignItems="center">
            <Grid item md={9}>
              <Box mt={1} mb={2}>
                <Typography className={classes.pageHeading}> {pageHeading} </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={3} direction="row">
              <Box textAlign="right" mt={1} mb={1}>
                <CommonButton cssClass="customSubmitBtn" onClick={() => resetForm()}>
                  Reset
                </CommonButton>
                <CommonButton
                  cssClass="customSubmitBtn"
                  isGreen
                  onClick={() => formik.submitForm()}
                >
                  Save
                </CommonButton>
              </Box>
            </Grid>
          </Grid>
          <Box style={{ marginBottom: "10px" }}>
            <Divider />
          </Box>

          <>
            <Grid spacing={2} container direction="column">
              <Grid item xs={12} md={12}>
                <Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(formik.values.overStayNotification)}
                        onChange={(event) => {
                          formik.setFieldValue("overStayNotification", event.target.checked);
                        }}
                        name="overStayNotification"
                        color="primary"
                      />
                    }
                    label="Vehicle over-stay notifications"
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={12}>
                <Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(formik.values.illicitFactorNotification)}
                        onChange={(event) => {
                          formik.setFieldValue("illicitFactorNotification", event.target.checked);
                        }}
                        name="illicitFactorNotification"
                        color="primary"
                      />
                    }
                    label="Illicit factor notifications"
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={10} xl={8} lg={8}>
                <Box style={{ display: "flex", alignItems: "center" }}>
                  <Typography style={{ width: "540px" }}>First notification time: </Typography>
                  <TextField
                    disabled={!formik.values.overStayNotification}
                    className={"customTextField"}
                    InputProps={{ inputProps: { min: 0 } }}
                    size="small"
                    variant="outlined"
                    fullWidth
                    InputLabelProps={{
                      shrink: true,
                    }}
                    type="number"
                    name="Hours"
                    label="Hours"
                    placeholder="Hours"
                    value={formik.values.fistNotificationHour}
                    onChange={(e) => formik.setFieldValue("fistNotificationHour", e.target.value)}
                    onKeyDown={(evt) =>
                      (evt.key === "-" ||
                        evt.key === "+" ||
                        evt.key === "e" ||
                        evt.key === "E" ||
                        evt.key === ".") &&
                      evt.preventDefault()
                    }
                    onCopy={disableCopy}
                    onPaste={disablePaste}
                    onCut={disableCopy}
                    error={
                      formik.touched.fistNotificationHour &&
                      Boolean(formik.errors.fistNotificationHour)
                    }
                    helperText={
                      formik.touched.fistNotificationHour && formik.errors.fistNotificationHour
                    }
                  />
                  <TextField
                    required={
                      formik.values.fistNotificationHour == 0 &&
                      formik.values.overStayNotification == true
                    }
                    disabled={!formik.values.overStayNotification}
                    style={{ marginLeft: "25px" }}
                    className={"customTextField"}
                    InputProps={{ inputProps: { min: 0 } }}
                    size="small"
                    variant="outlined"
                    fullWidth
                    InputLabelProps={{
                      shrink: true,
                    }}
                    type="number"
                    name="Minutes"
                    placeholder="Minutes"
                    label="Minutes"
                    value={formik.values.fistNotificationMinuts}
                    onChange={(e) => formik.setFieldValue("fistNotificationMinuts", e.target.value)}
                    onKeyDown={(evt) =>
                      (evt.key === "-" ||
                        evt.key === "+" ||
                        evt.key === "e" ||
                        evt.key === "E" ||
                        evt.key === ".") &&
                      evt.preventDefault()
                    }
                    onCopy={disableCopy}
                    onPaste={disablePaste}
                    onCut={disableCopy}
                    error={
                      formik.touched.fistNotificationMinuts &&
                      Boolean(formik.errors.fistNotificationMinuts)
                    }
                    helperText={
                      formik.touched.fistNotificationMinuts && formik.errors.fistNotificationMinuts
                    }
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={10} xl={8} lg={8}>
                <Box style={{ display: "flex", alignItems: "center" }}>
                  <Typography style={{ width: "540px" }}>Reminder notification time: </Typography>
                  <TextField
                    // style={{ marginLeft: "20px" }}
                    disabled={!formik.values.overStayNotification}
                    className={"customTextField"}
                    InputProps={{ inputProps: { min: 0 } }}
                    size="small"
                    variant="outlined"
                    fullWidth
                    InputLabelProps={{
                      shrink: true,
                    }}
                    type="number"
                    name="Hours"
                    label="Hours"
                    placeholder="Hours"
                    value={formik.values.reminderNotificationHour}
                    onChange={(e) =>
                      formik.setFieldValue("reminderNotificationHour", e.target.value)
                    }
                    onKeyDown={(evt) =>
                      (evt.key === "-" ||
                        evt.key === "+" ||
                        evt.key === "e" ||
                        evt.key === "E" ||
                        evt.key === ".") &&
                      evt.preventDefault()
                    }
                    onCopy={disableCopy}
                    onPaste={disablePaste}
                    onCut={disableCopy}
                    error={
                      formik.touched.reminderNotificationHour &&
                      Boolean(formik.errors.reminderNotificationHour)
                    }
                    helperText={
                      formik.touched.reminderNotificationHour &&
                      formik.errors.reminderNotificationHour
                    }
                  />
                  <TextField
                    required={
                      formik.values.reminderNotificationHour == 0 &&
                      formik.values.overStayNotification == true
                    }
                    disabled={!formik.values.overStayNotification}
                    style={{ marginLeft: "25px" }}
                    className={"customTextField"}
                    InputProps={{ inputProps: { min: 0 } }}
                    size="small"
                    variant="outlined"
                    fullWidth
                    InputLabelProps={{
                      shrink: true,
                    }}
                    type="number"
                    name="Minutes"
                    placeholder="Minutes"
                    label="Minutes"
                    value={formik.values.reminderNotificationMinuts}
                    onChange={(e) =>
                      formik.setFieldValue("reminderNotificationMinuts", e.target.value)
                    }
                    onKeyDown={(evt) =>
                      (evt.key === "-" ||
                        evt.key === "+" ||
                        evt.key === "e" ||
                        evt.key === "E" ||
                        evt.key === ".") &&
                      evt.preventDefault()
                    }
                    onCopy={disableCopy}
                    onPaste={disablePaste}
                    onCut={disableCopy}
                    error={
                      formik.touched.reminderNotificationMinuts &&
                      Boolean(formik.errors.reminderNotificationMinuts)
                    }
                    helperText={
                      formik.touched.reminderNotificationMinuts &&
                      formik.errors.reminderNotificationMinuts
                    }
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={10} xl={8} lg={8}>
                <Box style={{ display: "flex", alignItems: "center" }}>
                  <Typography style={{ width: "260px" }}>Camera Stream: </Typography>
                  <TextField
                    // style={{ marginLeft: "20px" }}
                    className={"customTextField"}
                    InputProps={{ inputProps: { min: 0 } }}
                    size="small"
                    variant="outlined"
                    fullWidth
                    InputLabelProps={{
                      shrink: true,
                    }}
                    name="Camera Stream"
                    placeholder="Camera Stream"
                    value={formik.values.cameraFeed}
                    onChange={(e) => formik.setFieldValue("cameraFeed", e.target.value)}
                    error={formik.touched.cameraFeed && Boolean(formik.errors.cameraFeed)}
                    helperText={formik.touched.cameraFeed && formik.errors.cameraFeed}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={10} xl={8} lg={8}>
                <Box style={{ display: "flex", alignItems: "center" }}>
                  <Typography style={{ width: "260px" }}>BT Alias: </Typography>
                  <TextField
                    // style={{ marginLeft: "53px" }}
                    className={"customTextField"}
                    InputProps={{ inputProps: { min: 0 } }}
                    size="small"
                    fullWidth
                    variant="outlined"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    name="BT Alias"
                    placeholder="BT Alias"
                    value={formik.values.btAllias}
                    onChange={(e) => formik.setFieldValue("btAllias", e.target.value)}
                    error={formik.touched.btAllias && Boolean(formik.errors.btAllias)}
                    helperText={formik.touched.btAllias && formik.errors.btAllias}
                  />
                </Box>
              </Grid>
            </Grid>
          </>
        </CardContent>
      </Card>
    </>
  );
}
